/**
 * Read-only WYSIWYG render of a canvas label layout (absolute mm
 * positioning), used so the Label Printer preview matches exactly what
 * the Tecor prints when a site has a canvas layout enabled for the
 * label type. Multi-page designs render each physical label stacked.
 */
import { QRCodeSVG } from "qrcode.react";
import { PX_PER_MM, elementValue, elTextStyle } from "@/lib/labelCanvas";

function LabelFace({ elements, data, W, H, pageIndex, pageCount }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="relative bg-white border-2 border-dashed border-eucalyptus-700/40 overflow-hidden print-area"
        style={{ width: W, height: H }}
        data-testid={pageIndex === 0 ? "wf-canvas-preview" : `wf-canvas-preview-${pageIndex}`}
      >
        {elements.map((el) => {
          if (el.visible === false) return null;
          const left = (el.x || 0) * PX_PER_MM;
          const top = (el.y || 0) * PX_PER_MM;

          if (el.type === "qr") {
            const val = el.source === "trust" ? (data?.trust_url || "") : (data?.qr_url || data?.barcode || "");
            const px = (el.cell || 4) * 8;
            return (
              <div key={el.id} className="absolute" style={{ left, top }}>
                {val ? (
                  <QRCodeSVG value={String(val)} size={px} level="M" marginSize={0} />
                ) : (
                  <div className="border border-black grid place-items-center text-[6px] font-bold" style={{ width: px, height: px }}>
                    {el.source === "trust" ? "TRUST QR" : "QR"}
                  </div>
                )}
              </div>
            );
          }

          if (el.type === "barcode") {
            const val = data?.barcode || "";
            return (
              <div key={el.id} className="absolute flex flex-col items-start" style={{ left, top }}>
                <div className="flex items-end gap-[1px] h-8" aria-hidden>
                  {String(val || "000000000000").split("").map((c, i) => (
                    <span key={i} className="bg-black" style={{ width: (Number(c) % 3) + 1, height: "100%" }} />
                  ))}
                </div>
                <div className="font-mono text-[8px] tracking-widest">{val}</div>
              </div>
            );
          }

          return (
            <div
              key={el.id}
              className="absolute leading-tight"
              style={{ left, top, ...elTextStyle(el, W) }}
            >
              {elementValue(el, data)}
            </div>
          );
        })}
      </div>
      {pageCount > 1 && (
        <span className="text-[10px] font-mono text-charcoal-soft">Label {pageIndex + 1} of {pageCount}</span>
      )}
    </div>
  );
}

export default function CanvasLabelView({ layout, data, widthMm = 60, heightMm = 50 }) {
  if (!layout) return null;
  const W = (layout.width_mm || widthMm) * PX_PER_MM;
  const H = (layout.height_mm || heightMm) * PX_PER_MM;
  const els = layout.elements || [];
  const pageCount = els.reduce((m, e) => Math.max(m, (e.page || 0) + 1), 1);

  return (
    <div className="flex flex-col items-center gap-3" data-print-scope="workflow">
      {Array.from({ length: pageCount }).map((_, p) => (
        <LabelFace
          key={p}
          elements={els.filter((e) => (e.page || 0) === p)}
          data={data}
          W={W}
          H={H}
          pageIndex={p}
          pageCount={pageCount}
        />
      ))}
    </div>
  );
}
